import { mapGetters } from 'vuex';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  maxWidgetsInDashboard,
  conversationSubmetrics,
  conversationResolutionReasonSubmetrics,
} from '../helpers/CustomDashboardsHelper';
import { useAlert } from 'dashboard/composables';
import MessagesComponent from 'dashboard/routes/dashboard/settings/reports/components/customDashboards/metricComponents/messages/Messages.vue';
import ConversationsComponent from 'dashboard/routes/dashboard/settings/reports/components/customDashboards/metricComponents/conversations/Conversations.vue';
import ConversationResolutionReasonsComponent from 'dashboard/routes/dashboard/settings/reports/components/customDashboards/metricComponents/conversationResolutionReasons/ConversationResolutionReasons.vue';
import StaticComponent from 'dashboard/routes/dashboard/settings/reports/components/customDashboards/metricComponents/static/Static.vue';
// import ExampleComponent from 'dashboard/routes/dashboard/settings/reports/components/customDashboards/metricComponents/example/Example.vue';

export default {
  components: {
    ConversationsComponent,
    MessagesComponent,
    ConversationResolutionReasonsComponent,
    StaticComponent,
  },
  computed: {
    ...mapGetters({
      customDashboards: 'customDashboards/getCustomDashboards',
      customDashboardsAlreadyFetchedOnMount:
        'customDashboards/getFetchedRecordsOnMountState',
      currentAccountId: 'getCurrentAccountId',
    }),
    // LISTA DE WIDGETS POSSÍVEIS
    // Novos widgets devem ter suas informações acrescentadas aqui
    widgetOptionsList() {
      return [
        /**
         *  ! O widget 'example' não deve ser usado em prod, só deve ser uma opção em devs
         *    O propósito dele é servir de norte para criação de novos widgets/métricas
         */
        // {
        //   type: 'example',
        //   title: 'Métrica Exemplo (dev)',
        //   component: ExampleComponent,
        //   displayOptions: ['summary'],
        //   possibleFilters: ['inbox'],
        // },

        {
          type: 'conversations',
          title: this.$t('CUSTOM_DASHBOARDS.WIDGET_TYPES.CONVERSATIONS'),
          component: ConversationsComponent,
          displayOptions: ['summary', 'column_chart'],
          possibleFilters: ['inbox'],
        },

        {
          type: 'conversationResolutionReason',
          title: this.$t(
            'CUSTOM_DASHBOARDS.WIDGET_TYPES.CONVERSATION_RESOLUTION_REASONS'
          ),
          component: ConversationResolutionReasonsComponent,
          displayOptions: ['summary', 'donut', 'table'],
          possibleFilters: ['inbox'],
        },

        {
          type: 'messages',
          title: this.$t('CUSTOM_DASHBOARDS.WIDGET_TYPES.MESSAGES'),
          component: MessagesComponent,
          displayOptions: ['summary', 'donut', 'column_chart'],
          possibleFilters: ['inbox'],
        },

        {
          type: 'static',
          title: this.$t('CUSTOM_DASHBOARDS.WIDGET_TYPES.STATIC'),
          component: StaticComponent,
          displayOptions: ['static_text', 'static_image'],
          possibleFilters: [''],
        },
      ];
    },
    // Caso haja nova implementação de DISPLAY para os widgets, adicionar aqui essa opção
    displaysList() {
      return [
        {
          type: 'summary',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.SUMMARY'),
        },
        {
          type: 'donut',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.DONUT'),
        },
        {
          type: 'column_chart',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.COLUMN'),
        },
        {
          type: 'pie',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.PIE'),
        },
        {
          type: 'table',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.TABLE'),
        },
        {
          type: 'static_text',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.STATIC_TEXT'),
        },
        {
          type: 'static_image',
          title: this.$t('CUSTOM_DASHBOARDS.DISPLAY_TYPES.STATIC_IMAGE'),
        },
        // { type: 'bar', title: 'Gráfico de Barras' },
      ];
    },
    // Retorna os displays possíveis para a opção de widget atual
    currentDisplaysList() {
      let displayOptions = this.widgetOptionsList.find(
        e => e.type === this.widgetBeingEdited.type
      )?.displayOptions;

      // Retiro a opção de display static_image se já houver algum widget estático de imagem no dashboard
      // Mas deixo a opção no widget em que ela está selecionada
      if (
        this.hasStaticImageWidgetOnDashboard &&
        this.widgetBeingEdited.type === 'static' &&
        !(this.widgetBeingEdited.contentProps.display.type === 'static_image')
      ) {
        displayOptions = displayOptions.filter(
          option => option !== 'static_image'
        );
      }

      return this.displaysList.filter(item =>
        displayOptions?.includes(item.type)
      );
    },
    conversationSubmetricsList() {
      return conversationSubmetrics.map(submetric => {
        const obj = {};
        obj.type = submetric.type;
        obj.title =
          this.$t(submetric.title.i18nKey) + submetric.title.additional;
        return obj;
      });
    },
    conversationResolutionReasonsSubmetricsList() {
      return conversationResolutionReasonSubmetrics.map(submetric => {
        const obj = {};
        obj.type = submetric.type;
        obj.title =
          this.$t(submetric.title.i18nKey) + submetric.title.additional;
        return obj;
      });
    },
  },
  mounted() {
    this.fetchCustomDashboardsIfFirstMounting();
  },
  methods: {
    fetchCustomDashboardsIfFirstMounting() {
      if (!this.customDashboardsAlreadyFetchedOnMount)
        this.$store.dispatch('customDashboards/get', true);
    },
    fetchCurrentCustomDashboards() {
      this.$store.dispatch('customDashboards/get', true);
    },
    // Function to redirect user to specific dashboard
    // If customDashboardId is empty it will redirect to the custom dashboard creation page
    redirectToDashboardPage(customDashboardId = null) {
      let url = `accounts/${this.currentAccountId}/reports/custom_dashboards`;
      if (customDashboardId) url += `/${customDashboardId}`;
      this.$router.push(frontendURL(url));
    },

    // Must receive a widget and return its properties (that will be saved in db)
    extractWidgetProperties(widget) {
      return {
        id: widget.id,
        col: widget.col,
        row: widget.row,
        sizeX: widget.sizeX,
        sizeY: widget.sizeY,
        header: widget.headerText,
        content: widget.content,
        type: widget.type,
        contentProps: widget.contentProps,
      };
    },
    // This function will be called right before adding a widget
    canAddMoreWidgets() {
      if (this.widgets.length >= maxWidgetsInDashboard) {
        useAlert(this.$t('CUSTOM_DASHBOARDS.ADD_WIDGET.MAX_MSG'));
        return false;
      }
      return true;
    },
  },
};
